import {
  Checkbox,
  FormControlLabel,
  Radio,
  makeStyles,
} from '@material-ui/core';
import React from 'react';
import { StyleSheet, View } from 'react-native';

import { colors as rootColors } from '../../../../constants/theme';
import {
  StoreProgramParticipantAdvance_storeProgramParticipantAdvance_row_checklistQuestions as ChecklistQuestion,
  StoreProgramParticipantAdvance_storeProgramParticipantAdvance_row_preferenceCheckList_checklistData,
} from '../../../../generated/StoreProgramParticipantAdvance';
import { QuestionTypeInputEnum } from '../../../../generated/globalTypes';
import { colors, spacing, theme } from '../../constants/theme';
import { Text, TextInput } from '../../core-ui';

import { UploadFieldInput } from './UploadFileInput';

export type ChecklistAnswer = StoreProgramParticipantAdvance_storeProgramParticipantAdvance_row_preferenceCheckList_checklistData;

// NOTE: multi choice answers are joined with this separator by the app
const CHECKLIST_SEPARATOR = ';';

type Props = {
  title: string;
  questionaire: Array<ChecklistQuestion>;
  answers?: Array<ChecklistAnswer>;
  editable?: boolean;
  onAnswerChange?: (question: ChecklistQuestion, answer: string) => void;
};

export function ProgramQuestionaireForm({
  title,
  questionaire,
  answers = [],
  editable = false,
  onAnswerChange,
}: Props) {
  let sortedQuestionaire = [...questionaire].sort((a, b) => a.order - b.order);

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text bold color="white" size="m">
          {title}
        </Text>
      </View>
      {sortedQuestionaire.map((question, index) => (
        <QuestionItem
          key={question.order}
          number={index + 1}
          question={question}
          answer={findAnswer(question, answers)}
          disabled={!editable}
          onChange={(answer) => onAnswerChange?.(question, answer)}
        />
      ))}
    </View>
  );
}

type QuestionItemProps = {
  number: number;
  question: ChecklistQuestion;
  answer: string;
  disabled: boolean;
  onChange: (answer: string) => void;
};

function QuestionItem({ number, ...answerProps }: QuestionItemProps) {
  let { question } = answerProps;
  return (
    <View style={styles.questionContainer}>
      <Text bold>
        {number}. {question.value}
        {question.required && <Text color="red">*</Text>}
      </Text>
      {!!question.notes && (
        <Text size="xs" color="grey" style={styles.notes}>
          {question.notes}
        </Text>
      )}
      <View style={styles.answerContainer}>
        <QuestionAnswer {...answerProps} />
      </View>
    </View>
  );
}

function QuestionAnswer({
  question,
  answer,
  disabled,
  onChange,
}: Omit<QuestionItemProps, 'number'>) {
  let options = [...question.options].sort((a, b) => a.order - b.order);
  let classes = useControlStyles();
  // NOTE: pass the icon only when disabled, MUI Radio spreads its props after
  // the default icon so `icon={undefined}` would hide the unchecked icon
  let controlProps = disabled
    ? { style: { color: theme.colors.text.grey } }
    : { classes: { root: classes.control, checked: classes.checked } };

  switch (question.type) {
    case QuestionTypeInputEnum.SINGLE_CHOICE: {
      return (
        <View style={styles.row}>
          {options.map((option) => (
            <View
              key={option.order}
              style={options.length > 2 ? styles.radioOptionFlex : undefined}
            >
              <FormControlLabel
                disabled={disabled}
                label={<Text>{option.value}</Text>}
                control={
                  <Radio
                    size="small"
                    {...controlProps}
                    {...(disabled && { icon: <DisabledUncheckedIcon round /> })}
                    checked={isSameAnswer(answer, option.value)}
                    onChange={() => onChange(option.value)}
                  />
                }
              />
            </View>
          ))}
        </View>
      );
    }
    case QuestionTypeInputEnum.MULTI_CHOICE: {
      let selected = splitMultiAnswer(answer);
      // NOTE: values that don't match any option are the "Lainnya" free text input
      let otherAnswers = selected.filter(
        (value) => !options.some((option) => isSameAnswer(value, option.value)),
      );
      // NOTE: selected values that match an option, without the free text
      let optionAnswers = selected.filter(
        (value) => !otherAnswers.includes(value),
      );
      let emitChange = (values: Array<string>) =>
        onChange(values.join(CHECKLIST_SEPARATOR));
      return (
        <View>
          {options.map((option) => {
            let isOtherOption = isOther(option);
            let checked =
              selected.some((value) => isSameAnswer(value, option.value)) ||
              (isOtherOption && otherAnswers.length > 0);
            let withoutOption = optionAnswers.filter(
              (value) => !isSameAnswer(value, option.value),
            );
            let onToggle = () => {
              if (checked) {
                emitChange(
                  isOtherOption
                    ? withoutOption
                    : [...withoutOption, ...otherAnswers],
                );
              } else {
                emitChange([...optionAnswers, option.value, ...otherAnswers]);
              }
            };
            let onOtherTextChange = (text: string) => {
              // NOTE: keep the "Lainnya" option itself while the text is empty
              // so the checkbox stays checked
              emitChange([...withoutOption, text.trim() ? text : option.value]);
            };
            return (
              <View key={option.order}>
                <FormControlLabel
                  disabled={disabled}
                  label={
                    <Text>
                      {option.value}
                      {disabled && isOtherOption && otherAnswers.length > 0
                        ? `: ${otherAnswers.join(', ')}`
                        : ''}
                    </Text>
                  }
                  control={
                    <Checkbox
                      size="small"
                      {...controlProps}
                      {...(disabled && { icon: <DisabledUncheckedIcon /> })}
                      checked={checked}
                      onChange={onToggle}
                    />
                  }
                />
                {!disabled && isOtherOption && checked && (
                  <OtherTextInput
                    initialValue={otherAnswers.join(', ')}
                    onChangeText={onOtherTextChange}
                  />
                )}
              </View>
            );
          })}
        </View>
      );
    }
    case QuestionTypeInputEnum.FILE_UPLOAD: {
      return (
        // NOTE: no title, the question text is already shown above
        <UploadFieldInput
          fileUrl={answer}
          onFileSelected={onChange}
          disabled={disabled}
        />
      );
    }
    case QuestionTypeInputEnum.FREE_TEXT:
    default: {
      return (
        <TextInput value={answer} onChangeText={onChange} disabled={disabled} />
      );
    }
  }
}

// NOTE: keep the raw text locally, the stored answer is trimmed when split so
// deriving the value from it would drop spaces while typing
function OtherTextInput({
  initialValue,
  onChangeText,
}: {
  initialValue: string;
  onChangeText: (text: string) => void;
}) {
  let [text, setText] = React.useState(initialValue);
  return (
    <TextInput
      value={text}
      onChangeText={(value) => {
        setText(value);
        onChangeText(value);
      }}
    />
  );
}

// NOTE: grey filled icon so unchecked options don't look clickable
function DisabledUncheckedIcon({ round }: { round?: boolean }) {
  return (
    <View style={styles.iconWrapper}>
      <View
        style={[styles.uncheckedIcon, round ? styles.round : styles.square]}
      />
    </View>
  );
}

function normalize(text: string) {
  return text.trim().toLowerCase();
}

function isSameAnswer(a: string, b: string) {
  return normalize(a) === normalize(b);
}

type AnswerData = Pick<ChecklistAnswer, 'question' | 'order' | 'answer'>;

export function findAnswer(
  question: ChecklistQuestion,
  answers: Array<AnswerData>,
) {
  let found =
    answers.find(({ order }) => order === question.order) ??
    answers.find(({ question: text }) => isSameAnswer(text, question.value));
  return found?.answer ?? '';
}

// NOTE: replace the answer of the given question, or add it if it doesn't exist yet
export function setChecklistAnswer(
  answers: Array<ChecklistAnswer>,
  question: ChecklistQuestion,
  answer: string,
): Array<ChecklistAnswer> {
  let index = answers.findIndex(({ order }) => order === question.order);
  if (index === -1) {
    index = answers.findIndex(({ question: text }) =>
      isSameAnswer(text, question.value),
    );
  }
  if (index === -1) {
    return [
      ...answers,
      {
        __typename: 'ChecklistData',
        question: question.value,
        order: question.order,
        answer,
      },
    ];
  }
  return answers.map((item, idx) =>
    idx === index ? { ...item, answer } : item,
  );
}

function isOther(option: ChecklistQuestion['options'][0]) {
  return option.isOther || isSameAnswer(option.value, 'Lainnya');
}

// NOTE: required questions must be answered, and a chosen "Lainnya" option must
// have its free text filled
export function isChecklistComplete(
  questionaire: Array<ChecklistQuestion>,
  answers: Array<AnswerData>,
) {
  return questionaire.every((question) => {
    let answer = findAnswer(question, answers);
    if (question.required && !answer.trim()) {
      return false;
    }
    if (question.type === QuestionTypeInputEnum.MULTI_CHOICE) {
      // NOTE: the "Lainnya" option value itself is only stored while its text is empty
      let otherOptions = question.options.filter(isOther);
      return !splitMultiAnswer(answer).some((value) =>
        otherOptions.some((option) => isSameAnswer(value, option.value)),
      );
    }
    return true;
  });
}

// NOTE: the "Lainnya" option value is only kept in the form state to keep its
// checkbox checked, it shouldn't be sent as an answer
export function getSubmittedAnswer(
  question: ChecklistQuestion,
  answer: string,
) {
  if (question.type !== QuestionTypeInputEnum.MULTI_CHOICE) {
    return answer;
  }
  let otherOptions = question.options.filter(isOther);
  return splitMultiAnswer(answer)
    .filter(
      (value) =>
        !otherOptions.some((option) => isSameAnswer(value, option.value)),
    )
    .join(CHECKLIST_SEPARATOR);
}

function splitMultiAnswer(answer: string) {
  return answer
    .split(CHECKLIST_SEPARATOR)
    .map((value) => value.trim())
    .filter(Boolean);
}

const useControlStyles = makeStyles({
  control: {
    color: rootColors.radio.border,
    '&$checked': { color: rootColors.radio.selected },
  },
  checked: {},
});

const styles = StyleSheet.create({
  container: {
    backgroundColor: colors.background.white,
    borderWidth: 1,
    borderColor: theme.colors.border.primary,
  },
  header: {
    backgroundColor: colors.background.blue,
    paddingVertical: spacing.s,
    paddingHorizontal: spacing.m,
    alignItems: 'center',
  },
  questionContainer: {
    paddingVertical: spacing.s,
    paddingHorizontal: spacing.m,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border.primary,
  },
  notes: {
    marginTop: spacing.xxs,
  },
  answerContainer: {
    marginTop: spacing.s,
    paddingLeft: spacing.m,
  },
  row: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  iconWrapper: {
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  uncheckedIcon: {
    width: 15,
    height: 15,
    borderWidth: 2,
    borderColor: theme.colors.text.grey,
    backgroundColor: theme.colors.textInput.background.disabled,
  },
  square: {
    borderRadius: 2,
  },
  round: {
    borderRadius: 8,
  },
  radioOptionFlex: {
    flex: 1,
    minWidth: 60,
  },
});
